import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { useThemeStore } from './stores/themeStore'
import { useEffect } from 'react'

// Layouts
import MainLayout from './components/layout/MainLayout'
import AuthLayout from './components/layout/AuthLayout'

// Auth Pages
import LoginPage from './features/auth/pages/LoginPage'
import OTPVerifyPage from './features/auth/pages/OTPVerifyPage'

// Main Pages
import DashboardPage from './features/dashboard/pages/DashboardPage'
import DoctorsListPage from './features/doctors/pages/DoctorsListPage'
import DoctorDetailPage from './features/doctors/pages/DoctorDetailPage'
import CatalogListPage from './features/catalog/pages/CatalogListPage'
import WorkOrdersListPage from './features/work-orders/pages/WorkOrdersListPage'
import WorkOrderDetailPage from './features/work-orders/pages/WorkOrderDetailPage'
import NewWorkOrderPage from './features/work-orders/pages/NewWorkOrderPage'
import InvoicesListPage from './features/invoices/pages/InvoicesListPage'
import InvoiceDetailPage from './features/invoices/pages/InvoiceDetailPage'
import NewInvoicePage from './features/invoices/pages/NewInvoicePage'
import PaymentsListPage from './features/payments/pages/PaymentsListPage'
import DuesOverviewPage from './features/statements/pages/DuesOverviewPage'
import MonthlyStatementPage from './features/statements/pages/MonthlyStatementPage'
import ReportsPage from './features/reports/pages/ReportsPage'
import SettingsPage from './features/settings/pages/SettingsPage'
import AuditLogPage from './features/audit/pages/AuditLogPage'
import UsersListPage from './features/users/pages/UsersListPage'
import AddressMasterPage from './features/admin/pages/AddressMasterPage'

// Legal Pages
import PrivacyPolicyPage from './features/legal/PrivacyPolicyPage'
import TermsOfServicePage from './features/legal/TermsOfServicePage'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  const { theme } = useThemeStore()

  useEffect(() => {
    // Apply theme class to document
    document.documentElement.classList.remove('light', 'dark', 'nature', 'high-contrast')
    document.documentElement.classList.add(theme)
  }, [theme])

  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<OTPVerifyPage />} />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Doctors */}
        <Route path="/doctors" element={<DoctorsListPage />} />
        <Route path="/doctors/:id" element={<DoctorDetailPage />} />

        {/* Catalog */}
        <Route path="/catalog" element={<CatalogListPage />} />

        {/* Work Orders */}
        <Route path="/work-orders" element={<WorkOrdersListPage />} />
        <Route path="/work-orders/new" element={<NewWorkOrderPage />} />
        <Route path="/work-orders/:id" element={<WorkOrderDetailPage />} />

        {/* Invoices */}
        <Route path="/invoices" element={<InvoicesListPage />} />
        <Route path="/invoices/new" element={<NewInvoicePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />

        {/* Payments */}
        <Route path="/payments" element={<PaymentsListPage />} />

        {/* Statements */}
        <Route path="/statements" element={<DuesOverviewPage />} />
        <Route path="/statements/monthly" element={<MonthlyStatementPage />} />

        {/* Reports */}
        <Route path="/reports" element={<ReportsPage />} />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />

        {/* Audit */}
        <Route path="/audit" element={<AuditLogPage />} />

        {/* User Management */}
        <Route path="/users" element={<UsersListPage />} />

        {/* Admin */}
        <Route path="/admin/address" element={<AddressMasterPage />} />
      </Route>

      {/* Legal Pages (Public) */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
